/**
 * 需要グループ CSV の雛形 (Step1「テンプレートをダウンロード」、
 * common.md AC-DUC-031 / AC-DUC-035、記入例定義は
 * `docs/specs/admin/data/uploads/demand-groups.md` §1「雛形の記入例」)。
 *
 * 記入例は代表 2 パターン (spec §1 表下の注記を逐語):
 *   ① グループ新設: 同一の (需要BGコード, 小売事業者コード, グループ名) で
 *     供給地点特定番号が異なる複数行 (2 行)
 *   ② 削除フラグ = `1` による所属の論理削除行 (AC-DUG-009、1 行)
 *
 * **不変条件: マスタ依存列 (需要BGコード・小売事業者コード) のダミー値は
 * OCCTO 採番規則に適合させない。** backend `parse_csv.go` の
 * `validateCodeFormats` は需要BGコード (5桁: 区分1桁+英数3桁+エリアコード1桁、
 * `valueobject.ParseBgCode`) / 小売事業者コード (4桁の英大文字・数字、
 * `valueobject.ParseBusinessCode`) の書式のみを検証し、書式が不正な行が
 * 1 件でもあれば CSV 全体を Check RPC で InvalidArgument 拒否する
 * (`checkError` 誘導)。需要BGマスタ・小売事業者マスタへの突合
 * (spec AC-DUG-002/003) は現行 backend 未実装 (slice 4/5 scope) のため、
 * **もしダミー値が書式に適合していたら Check を通過し、Apply で実グループ
 * として登録されてしまう** (存在チェックが無いため)。書式不適合のダミー値を
 * 保つことが、記入例 ① (削除フラグ空) をそのまま入稿しても誤登録が起きない
 * ことの唯一の担保。記入例 ② (削除フラグ `1`) は現行 backend が書式検査より
 * 前の削除フラグガード (同 `parse_csv.go`、論理削除 = slice 5 未実装) で
 * CSV 全体を拒否するため、② を含む入稿は書式に関わらず Check を通らない —
 * ② を雛形に載せるのは spec §1 が記入例として要求するためで、slice 5 まで
 * 「見本どおりに書くと現行では全体拒否される」状態が続く (map #2946)。
 * 供給地点特定番号は 22 桁の未使用番号にする (現状は必須 + 22桁の DB 幅
 * ガードのみで、マスタ突合〈AC-DUG-004〉は同じく未実装)。
 */
import type { DemandGroupCsvRow } from '~/models/demandGroupCsvHeader'
import {
  demandGroupCsvDataTypeName,
  serializeDemandGroupCsvRows,
} from '~/models/demandGroupCsvHeader'

/**
 * 雛形記入例 3 行 (①×2 行 + ②×1 行)。AC-DUC-035 の代表列・代表値
 * (D6、Design Note `upload-wizard-step1-panel.md`) は行1 (① の 1 行目) の
 * 「需要BGコード」を使う。
 */
export const DEMAND_GROUP_CSV_TEMPLATE_ROWS: DemandGroupCsvRow[] = [
  // ① グループ新設 (1 行目): 供給地点1
  {
    demandBgCode: 'SAMPLE-BG-001',
    retailerCode: 'SAMPLE-RETAILER-001',
    groupName: 'サンプルグループ1',
    supplyPointSpecificNumber: '0000000000000000000001',
    name: 'サンプル地点1',
    deleteFlag: '',
  },
  // ① グループ新設 (2 行目): 同一グループ鍵 (需要BGコード・小売事業者コード・
  // グループ名) で供給地点特定番号だけが異なる行。
  {
    demandBgCode: 'SAMPLE-BG-001',
    retailerCode: 'SAMPLE-RETAILER-001',
    groupName: 'サンプルグループ1',
    supplyPointSpecificNumber: '0000000000000000000002',
    name: 'サンプル地点2',
    deleteFlag: '',
  },
  // ② 削除フラグ = 1 による所属の論理削除行 (AC-DUG-009)。① と鍵を分け、
  // 「新設グループの一部を同時に削除している」ように誤読させない。
  {
    demandBgCode: 'SAMPLE-BG-002',
    retailerCode: 'SAMPLE-RETAILER-001',
    groupName: 'サンプルグループ2',
    supplyPointSpecificNumber: '0000000000000000000003',
    name: 'サンプル地点3',
    deleteFlag: '1',
  },
]

/**
 * 雛形ダウンロード用の CSV 本文を組み立てる (ヘッダー行 + 記入例3行)。
 * 列組み立ては入稿済みデータ DL (`~/pages/AdminUploads/models/demandGroupCsvExport`)
 * と共有する `~/models/demandGroupCsvHeader` の `serializeDemandGroupCsvRows`
 * (単一 SoT) に委譲する。ヘッダー自体もそこが持つ `DEMAND_GROUP_CSV_HEADER`
 * (日本語表示) / `DEMAND_GROUP_CSV_HEADER_EN` (英語表示) から生成され、どちらも
 * 自画面の DropZone が受理するヘッダー (`acceptedHeaders` の ja / en) と同じ定数
 * なので、DL した雛形が自画面の厳格チェックを通る不変条件が保たれる (D4)。
 */
export function buildDemandGroupCsvTemplate(isEnglish: boolean): string {
  // 英語表示はヘッダー行を英語ヘッダーにする (common.md §6)。記入例のグループ名・名称は
  // 自由記入のダミー値で、英語の値が spec に無いので訳さない (`conventions.md`「CSV の英語表示」)。
  return serializeDemandGroupCsvRows(DEMAND_GROUP_CSV_TEMPLATE_ROWS, isEnglish)
}

/** 雛形のファイル名 (`{データ種別名}_テンプレート.csv` / 英語表示は `_template.csv`)。 */
export function demandGroupCsvTemplateFileName(isEnglish: boolean): string {
  return `${demandGroupCsvDataTypeName(isEnglish)}_${isEnglish ? 'template' : 'テンプレート'}.csv`
}
